import { Box, Button, CircularProgress, IconButton, Typography, useTheme } from '@mui/material'
import React, { useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { convert } from 'html-to-text'
import { observer } from 'mobx-react'
import Latex from 'react-latex-next'
import { v4 as uuidv4 } from 'uuid'

import { Icons, latexDelimiters } from '../../../utils/utilities'
import VisuallyHiddenInput from '../../common/VisuallyHiddenInput'
import { useMeetingStore } from '../../../store/meeting.store'
import CustomTooltip from '../../common/CustomTooltip'
import CustomChip from '../../common/CustomChip'
import { useParams } from 'react-router-dom'
import { useSocketsContext } from '../../../contexts/sockets.context'
import { useOnlineSessionsSocketStore } from '../../../store/onlinesessionsocket.store'

const extractPollQuestions = ( data ) => {
    let preview = []
    let questions = []
    let errors = {}
    let errorLines = []
    let lines
    lines = data.split( "\n" )
    let question = null
    let options = []
    let correctAnswer = null
    let answersStatus = []
    let answers = []
    for ( let i = 0; i < lines.length; i++ ) {
        preview.push( `<p> ${lines[i]}</p>` )
        if ( lines[i] === "\r" || !lines[i]?.trim() ) {
            if ( question && options.length > 1 ) {
                const values = { id: uuidv4(), question: question, marks: 1, questionType: "MCQ", selectionType: "single", options, correctAnswer, answersStatus }
                questions.push( values )
            }

            question = null
            options = []
            answersStatus = []
            correctAnswer = null
            answers = []
        } else if ( lines[i].match( /^([A-Z][.)]\s)/ ) ) {
            const splitted = lines[i].split( /^([A-Z][.)]\s)/ )
            options.push( splitted[2] )
            answers.push( splitted[1][0] )
            answersStatus.push( false )
        } else if ( lines[i].match( /ANSWER: / ) ) {
            const answer = lines[i].split( ": " )[1][0]
            correctAnswer = answers.indexOf( answer )
            answersStatus[correctAnswer] = true
            if ( question && correctAnswer !== null && correctAnswer >= 0 && options.length > 1 ) {
                const values = { id: uuidv4(), question: question, marks: 1, questionType: "MCQ", selectionType: "single", options, correctAnswer, answersStatus }
                questions.push( values )
            } else {
                let messages = []
                if ( !question || question.trim() === "" )
                    messages.push( "- Question is not provided." )
                if ( correctAnswer === null || correctAnswer < 0 )
                    messages.push( "- Correct answer is not provided." )
                if ( options.length === 0 )
                    messages.push( "- Options not provided." )
                errors[i + 1] = { line: i + 1, messages }
                errorLines.push( i + 1 )
            }
            question = null
            options = []
            correctAnswer = null
            answersStatus = []
            answers = []
        } else {
            question = lines[i]
            if ( options.length > 0 || correctAnswer ) {
                errors[i + 1] = { line: i + 1, messages: ["- Answer or options not provided"] }
                errorLines.push( i + 1 )
            }
            options = []
            correctAnswer = null
            answers = []
            answersStatus = []
        }
    }
    return { questions, errors, preview, errorLines }
}


const PollQueue = observer( () => {

    const [questionsPreview, setQuestionsPreview] = useState( null )
    const [isImporting, setIsImporting] = useState( false )
    const [draggingId, setDraggingId] = useState( null )

    const { palette, layout, border } = useTheme()
    const { meeting_id } = useParams()

    // const { emitEvent } = useSocketsContext()

    const OnlineSessionSocketStore = useOnlineSessionsSocketStore()
    const MeetingStore = useMeetingStore()

    const dragInfo = useRef( { startY: 0, currentDelta: 0, element: null } )
    const pollQueueContainerRef = useRef()
    const questionFileInputRef = useRef()

    const handlePointerDown = ( e, pollId, index ) => {
        const row = e.currentTarget.closest( '.poll-queue-row' )
        row.setPointerCapture( e.pointerId )

        // Store initial drag state
        dragInfo.current = {
            startY: e.clientY,
            currentIndex: index,
            element: row,
            rowHeight: row.offsetHeight + 10 // Height + your Box gap (10px)
        }

        setDraggingId( pollId )
    }

    const handlePointerMove = ( e ) => {

        if ( !draggingId || !dragInfo.current.element ) return

        const deltaY = e.clientY - dragInfo.current.startY
        const { element, rowHeight, currentIndex } = dragInfo.current

        element.style.transform = `translateY(${deltaY}px)`
        element.style.zIndex = 100
        element.style.boxShadow = '0px 10px 20px rgba(0,0,0,0.3)'

        const currentSlotOffset = Math.round( deltaY / rowHeight )

        const allRows = pollQueueContainerRef.current.querySelectorAll( '.poll-queue-row' )

        allRows.forEach( ( row, index ) => {
            if ( index === currentIndex ) return

            let shift = 0
            if ( currentSlotOffset > 0 ) {
                if ( index > currentIndex && index <= currentIndex + currentSlotOffset ) {
                    shift = -rowHeight
                }
            } else if ( currentSlotOffset < 0 ) {
                if ( index < currentIndex && index >= currentIndex + currentSlotOffset ) {
                    shift = rowHeight
                }
            }

            row.style.transform = `translateY(${shift}px)`
            row.style.transition = 'transform 0.2s cubic-bezier(0.2, 0, 0, 1)'
        } )
    }

    const handlePointerUp = ( e ) => {

        if ( !draggingId ) {
            dragInfo.current.element = null
            dragInfo.current.startY = 0
            dragInfo.current.currentDelta = 0
            return
        }

        const deltaY = e.clientY - dragInfo.current.startY
        const { currentIndex, rowHeight, element } = dragInfo.current

        const slotsMoved = Math.round( deltaY / rowHeight )
        const newIndex = Math.max( 0, Math.min(
            MeetingStore.pollQueue.length - 1,
            currentIndex + slotsMoved
        ) )

        // 1. Reset all DOM styles immediately to avoid "ghost" offsets
        const allRows = pollQueueContainerRef.current.querySelectorAll( '.poll-queue-row' )
        allRows.forEach( row => {
            row.style.transform = ''
            row.style.transition = ''
            row.style.zIndex = ''
            row.style.boxShadow = ''
        } )

        // 2. Commit to MobX Store
        if ( newIndex !== currentIndex ) {
            console.log( {
                from: currentIndex,
                to: newIndex
            } )

            MeetingStore.updatePollQueue( "reorder", {
                from: currentIndex,
                to: newIndex
            } )
        }

        setDraggingId( null )
        dragInfo.current.element = null
        dragInfo.current.startY = 0
        dragInfo.current.currentDelta = 0
    }

    const onQuestionFileSelect = ( event ) => {
        try {
            const file = event.target.files[0]

            if ( !file.name?.endsWith( "txt" ) )
                return toast( `${file.name} is not supported! Please select a text file which contains questions in AIKEN format.` )

            const fileReader = new FileReader()
            fileReader.onload = () => {
                const { questions, errors, preview, errorLines } = extractPollQuestions( fileReader.result )
                setQuestionsPreview( { status: "loaded", data: preview, file: file, type: "txt", plainData: fileReader.result, errors, questions, errorLines } )
                event.target.value = ""
            }
            fileReader.readAsText( file )
        } catch ( err ) {
            toast( "Failed to load question file!" )
        }
    }

    const importQuestions = () => {
        setIsImporting( true )

        const questions = questionsPreview.questions.map( question => {

            const details = {
                id: question.id,
                question: question.question,
                options: question.options,
                correct_answer: question.correctAnswer
            }

            return details
        } )

        MeetingStore.updatePollQueue( "add", questions )
        setQuestionsPreview( null )
        setIsImporting( false )
    }

    const postQueuedPoll = async ( pollIndex ) => {
        try {

            const pollDetails = MeetingStore.updatePollQueue( "remove", { index: pollIndex } )

            const { message, success, poll } = await OnlineSessionSocketStore.emit(
                'poll:create',
                {
                    room_id: meeting_id,
                    question: pollDetails.question,
                    options: pollDetails.options,
                    duration: 60,
                    correct_answer: pollDetails.correct_answer
                } )

            if ( success ) {
                MeetingStore.addOrRemovePoll( poll )
            }
            if ( message )
                toast( message )
        } catch ( err ) {
            console.log( err )
        }
    }

    return (
        <Box>
            {!questionsPreview && <Box display="flex" flexDirection="column" gap={layout.sectionsGap}>
                {( !MeetingStore.getPollQueue || MeetingStore.getPollQueue?.length === 0 ) && <Box border={border[1]} borderColor={palette.primary.light} bgcolor={palette.primary.light + 22} borderRadius="10px" padding="10px" display="flex" alignItems="flex-start" gap="10px" >
                    <Typography component="span" fontSize="16px">{Icons.default.Info}</Typography>
                    <Typography component="p" fontSize="12px">Automate your polling by importing questions into the queue. Set a time interval to publish them sequentially, or manually trigger any question whenever you're ready.</Typography>
                </Box>}


                {( !MeetingStore.getPollQueue || MeetingStore.getPollQueue?.length === 0 ) && <Box padding="20px" display="flex" flexDirection={{ xs: "column", sm: "row" }} gap="20px" position="relative" alignItems="center" bgcolor={palette.contentBg} borderRadius="10px" border={`2px dashed ${palette.borderColor}`} sx={{ cursor: "pointer", ":hover": { borderColor: palette.text.secondary } }} >

                    <Box ref={questionFileInputRef} onChange={onQuestionFileSelect} component="input" type="file" accept='application/text' sx={{ opacity: 0, cursor: "pointer", zIndex: 10, position: "absolute", inset: "0" }} />

                    <Typography color='textSecondary' display="flex" alignItems="center" fontSize="30px">{Icons.default.CloudUpload}</Typography>
                    <Box display="flex" flexDirection="column" alignItems={{ xs: "center", sm: "flex-start" }} flexGrow={1}>
                        <Typography textAlign="center" fontSize="16px" fontWeight={600} component="p" >Drag and drop or <u>select</u> poll question file </Typography>
                        <Typography textAlign="center" fontSize="14px" color='textSecondary' component="span" > Supported format: <CustomChip containerStyles={{ PointerEvent: "none" }} value=".txt" /> </Typography>
                    </Box>
                    <Button variant='contained' onClick={() => questionFileInputRef.current?.click()} startIcon={Icons.default.UploadFileIcon} sx={{ display: "flex", borderRadius: "20px", position: "relative", zIndex: 10 }} disableElevation >Select File</Button>
                </Box>}

                {/* Poll Queue */}
                {MeetingStore.getPollQueue?.length > 0 && <Box>
                    <Box display="flex" justifyContent="space-between" gap={layout.sectionsGap} alignItems="flex-end" marginBottom="10px" >
                        <Typography variant='subtitle2' fontSize="12px" component="h2" color='textSecondary' letterSpacing="0.1em">POLL QUEUE</Typography>
                        <Button tabIndex={-1} component="label" size='small' variant='contained' color='primary' disableElevation sx={{ borderRadius: "20px" }} >
                            Import Poll Questions
                            <VisuallyHiddenInput accept='application/text' type="file" onChange={onQuestionFileSelect} />
                        </Button>

                    </Box>
                    <Box display="flex" flexDirection="column" ref={pollQueueContainerRef} gap={layout.cardsGap} onPointerUp={handlePointerUp} onPointerLeave={handlePointerUp} onPointerMove={handlePointerMove}>
                        {MeetingStore.getPollQueue?.map( ( poll, pollIndex ) => (
                            <Box className={`poll-queue-row ${draggingId === poll.id ? 'poll-item-dragging' : ''}`} key={poll.id} padding="10px" borderRadius="10px" display="flex" gap="20px" justifyContent="space-between" alignItems="flex-start" border={border[1]} >
                                {/* DRAG HANDLE */}
                                <Box display="flex" gap="10px" alignItems="flex-start">
                                    <Box
                                        marginTop="3px"
                                        className="poll-item-drag-handle"
                                        onPointerDown={( e ) => handlePointerDown( e, poll.id, pollIndex )}
                                        sx={{ color: palette.text.disabled, display: 'flex' }}
                                    >
                                        {Icons.default.DragIndicatorIcon}
                                    </Box>
                                    <Box marginTop="3px" fontSize="14px">
                                        <Latex delimiters={latexDelimiters}>{poll.question}</Latex>
                                    </Box>
                                </Box>
                                <Box display="flex" flexShrink={0} alignItems="center" gap="10px">
                                    <Button onClick={() => postQueuedPoll( pollIndex )} endIcon={Icons.default.ArrowOutward} variant='text' color='secondary' size='small' disableElevation sx={{ flexShrink: 0, borderRadius: "20px" }}>
                                        Post poll
                                    </Button>
                                    <IconButton size='small' sx={{ color: "GrayText", ":hover": { color: "error.main" } }} onClick={() => MeetingStore.updatePollQueue( "remove", { index: pollIndex } )}>{Icons.default.DeleteIcon}</IconButton>
                                </Box>
                            </Box>
                        ) )}
                    </Box>
                </Box>}

            </Box>}

            {questionsPreview?.status === "loaded" && <Box border={border[1]} overflow="clip" borderRadius="5px">
                <Box>
                    {questionsPreview && Object.keys( questionsPreview?.errors || {} ).length > 0 && <Box bgcolor={`${palette.error.light}22`} padding="10px">
                        {Object.keys( questionsPreview?.errors || {} ).map( line => {
                            const error = questionsPreview?.errors[line]
                            return (
                                <Box key={error.line}>
                                    <Typography color="error" variant="subtitle2">Line {error.line}</Typography>
                                    {error.messages.map( ( msg, i ) => (
                                        <Typography key={`key-${i}`} color="error" variant="subtitle2" marginLeft="10px">{msg}</Typography>
                                    ) )}
                                </Box>
                            )
                        } )}
                    </Box>}
                    <Box padding="10px" bgcolor={`${palette.success.light}22`}>
                        <Typography color="success.main" variant='subtitle2'>{questionsPreview?.questions.length} questions detected</Typography>
                    </Box>
                    <Box maxHeight="300px" overflow="auto">
                        {questionsPreview && questionsPreview.data.map( ( line, k ) => (
                            <Box minHeight="20px" marginTop="1px" key={`line-${k}`} position="relative" paddingLeft="35px" gap="20px" display="flex" height="100%" alignItems="center">
                                <Box bgcolor={questionsPreview?.errorLines.includes( k + 1 ) ? palette.error.light + 22 : palette.contentBg} borderRight={border[1]} fontSize="12px" display="flex" alignItems="center" justifyContent="center" position="absolute" left="0" height="100%" width="25px">{k + 1}</Box>
                                <CustomTooltip title={"ERROR" || questionsPreview.errors[k + 1] ? questionsPreview.errors[k + 1]?.messages?.join( "," ) : ""} >
                                    <Box borderBottom={questionsPreview?.errorLines.includes( k + 1 ) ? `2px dashed ${palette.error.light + 22}` : "none"} sx={{ "& >p": { margin: "5px 0" } }} fontSize="12px !important">{convert( line ).trim() !== "" ? <Latex delimiters={latexDelimiters}>{line}</Latex> : <p> &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</p>}</Box>
                                </CustomTooltip>
                            </Box>
                        ) )}
                    </Box>
                </Box>
                <Box borderTop={border[1]} bgcolor={palette.contentBg} justifyContent="flex-end" padding="10px 20px" gap="10px" alignItems="center" display="flex">
                    <Button size='small' onClick={() => setQuestionsPreview( null )} color='error' variant="outlined">Cancel</Button>
                    {questionsPreview && <Button size='small' disableElevation disabled={isImporting || Object.keys( questionsPreview?.errors || {} ).length > 0 || !questionsPreview?.questions || questionsPreview?.questions?.length === 0} startIcon={isImporting ? <CircularProgress size={14} /> : Icons.default.DownloadForOfflineIcon} onClick={async () => { importQuestions( questionsPreview.questions ) }} variant="contained">{isImporting ? "Importing questions..." : "Import"}</Button>}
                </Box>
            </Box>}
        </Box>
    )
} )

export default PollQueue
